// ApiCallMethod.js
const getToken = () => {
  const userData = JSON.parse(localStorage.getItem("user")) || {};
  const token = userData?.data?.AccessToken || "";
  return token;
};

const saveNewToken = (newAccessToken) => {
  const userData = JSON.parse(localStorage.getItem("user")) || {};
  userData.data = {
    ...userData.data,
    AccessToken: newAccessToken,
  };
  localStorage.setItem("user", JSON.stringify(userData));
};

const redirectToLogin = async () => {
  localStorage.clear();
  window.location.href = "/";
};

// The refresh token itself is never seen by this code — it lives in an
// httpOnly cookie the browser sends automatically (credentials: "include").
export const fetchNewAccessToken = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/api/auth/refresh-token`,
      {
        method: "POST",
        mode: "cors",
        credentials: "include",
      }
    );

    if (!response.ok) {
      throw new Error("Failed to refresh token");
    }

    const data = await response.json();
    saveNewToken(data?.token);
    return data?.token;
  } catch (error) {
    redirectToLogin();
  }
};

export const logoutApi = async () => {
  try {
    await fetch(`${process.env.REACT_APP_BASE_URL}/api/auth/logout`, {
      method: "POST",
      mode: "cors",
      credentials: "include",
    });
  } catch (error) {
    // Best-effort — localStorage still gets cleared either way.
    console.error("Logout API call failed:", error.message);
  }
};

const getHeader = () => {
  const headers = new Headers();
  const token = getToken();
  if (token) {
    headers.append("Authorization", `Bearer ${token}`);
  }
  headers.append("Access-Control-Allow-Origin", "*");
  headers.append("Content-Type", "application/json");
  return headers;
};

const getHeader2 = () => {
  const headers = new Headers();
  const token = getToken();
  if (token) {
    headers.append("Authorization", `Bearer ${token}`);
  }
  headers.append("Access-Control-Allow-Origin", "*");
  //headers.append("Content-Type", "application/json");
  return headers;
};

const handle401 = async (endpoint, options) => {
  try {
    const newToken = await fetchNewAccessToken();
    options.headers.set("Authorization", `Bearer ${newToken}`);
    return await fetch(endpoint, options); // Retry with the new token
  } catch (error) {
    throw error; // Handle token refresh failure
  }
};

const fetchWithRetry = async (endpoint, options) => {
  const response = await fetch(endpoint, options);

  if (response.status === 401) {
    return handle401(endpoint, options);
  }
  if (response.status === 403) {
    const data = await response.json();
    if (data?.error) {
      redirectToLogin();
      return;
    }
  }
  return response;
};

export const getMethod = (endpoint) => {
  const options = {
    method: "GET",
    mode: "cors",
    headers: getHeader(),
  };
  return fetchWithRetry(
    `${process.env.REACT_APP_BASE_URL}${endpoint}`,
    options
  );
};

export const getMethodNoAuth = (endpoint) => {
  const options = {
    method: "GET",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "Access-Control-Allow-Origin": "*",
    },
  };

  return fetch(`${process.env.REACT_APP_BASE_URL}${endpoint}`, options);
};

export const patchMethod = (endpoint, body) => {
  const options = {
    method: "PATCH",
    mode: "cors",
    headers: getHeader(),
    body: JSON.stringify(body),
  };
  return fetchWithRetry(
    `${process.env.REACT_APP_BASE_URL}${endpoint}`,
    options
  );
};

export const patchMethod2 = (endpoint, body) => {
  const headers = getHeader2();
  headers.delete("Content-Type");

  const options = {
    method: "PATCH",
    mode: "cors",
    headers: headers,
    body: body,
  };

  return fetchWithRetry(
    `${process.env.REACT_APP_BASE_URL}${endpoint}`,
    options
  );
};

export const postMethod = (endpoint, body) => {
  const options = {
    method: "POST",
    mode: "cors",
    headers: getHeader(),
    body: JSON.stringify(body),
  };
  return fetchWithRetry(
    `${process.env.REACT_APP_BASE_URL}${endpoint}`,
    options
  );
};
export const postMethod3 = async (endpoint, body, options = {}) => {
  const { suppress401 = false } = options;

  try {
    const res = await fetch(`${process.env.REACT_APP_BASE_URL}${endpoint}`, {
      method: "POST",
      mode: "cors",
      headers: getHeader(),
      body: JSON.stringify(body),
    });

    const data = await res.json();

    if (!res.ok) {
      const error = new Error(data?.message || "Request failed");
      error.status = res.status;
      error.response = data;
      error.suppress401 = suppress401; // pass this along
      throw error;
    }

    return data;
  } catch (error) {
    if (suppress401 && error.status === 401) {
      // Return controlled error
      return {
        __errorHandled: true,
        status: 401,
        message: error.response?.message,
      };
    }
    throw error;
  }
};

export const postMethod2 = (endpoint, body) => {
  const options = {
    method: "POST",
    mode: "cors",
    headers: getHeader2(),
    body: body,
  };
  return fetchWithRetry(
    `${process.env.REACT_APP_BASE_URL}${endpoint}`,
    options
  );
};

export const postMethodForReset = async (endpoint, body) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}${endpoint}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.message || "Something went wrong");
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    throw error;
  }
};

export const postMethodForSignin = async (endpoint, body) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}${endpoint}`,
      {
        method: "POST",
        mode: "cors",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
        body: JSON.stringify(body),
      }
    );

    return response;
  } catch (error) {
    console.error("Signin API call failed:", error.message);
    throw error;
  }
};

export const putMethod = (endpoint, body) => {
  const options = {
    method: "PUT",
    mode: "cors",
    headers: getHeader(),
    body: JSON.stringify(body),
  };
  return fetchWithRetry(
    `${process.env.REACT_APP_BASE_URL}${endpoint}`,
    options
  );
};

export const putMethod2 = (endpoint, body) => {
  const headers = getHeader2();
  headers.delete("Content-Type"); // Remove Content-Type if it exists

  const options = {
    method: "PUT",
    mode: "cors",
    headers: headers,
    body: body,
  };

  return fetchWithRetry(
    `${process.env.REACT_APP_BASE_URL}${endpoint}`,
    options
  );
};

export const deleteMethod = (endpoint) => {
  const options = {
    method: "DELETE",
    mode: "cors",
    headers: getHeader(),
  };
  return fetchWithRetry(
    `${process.env.REACT_APP_BASE_URL}${endpoint}`,
    options
  );
};
